import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Gift, QrCode, Sparkles } from 'lucide-react';

import { queryKeys } from '@/lib/api/queryKeys';
import { eventsApi } from '@/features/events/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { useAuthStore } from '@/lib/auth/authStore';

/**
 * Caso 11 — Booth-operator's single-station view. Mobile-first: big touch
 * targets, minimal chrome, just what someone running the table at an event
 * needs:
 *   - which survey is active
 *   - what stock is left per prize
 *   - shortcut to the canje flow (Caso 03) and the QR (existing page)
 *
 * Reuses `/xk-station/:id/summary` (already powers the full StationDetailPage
 * and the AI context). v1 uses regular JWT auth; the token-based access
 * (XkStationAccessToken) is the v2 follow-up per the PRD.
 */
export function OperatorStationPage() {
  const { id } = useParams<{ id: string }>();
  const stationId = Number(id);
  const navigate = useNavigate();
  const t = useT();
  useFavoritable(t('operator.title'));
  // An operator session (Caso 11) is scoped to a single station — there is
  // no station list to go "back" to. Admins reaching this page from the
  // dashboard, by contrast, do want the back link.
  const isOperatorSession = useAuthStore((s) => s.app) === 'operator';

  const summary = useQuery({
    queryKey: queryKeys.stations.summary(stationId),
    queryFn: () => eventsApi.getStationSummary(stationId),
    enabled: Number.isFinite(stationId) && stationId > 0,
  });

  if (!Number.isFinite(stationId) || stationId <= 0) {
    return <div className="page">{t('operator.invalidId')}</div>;
  }

  if (summary.isLoading || !summary.data) {
    return (
      <div className="page space-y-4 max-w-md">
        <Skeleton className="h-10" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  const { station, event, survey, inventory } = summary.data;
  const totalRemaining = inventory.reduce((s, i) => s + (i.available ?? 0), 0);
  const lowStock = inventory.filter(
    (i) => typeof i.available === 'number' && i.available <= 3,
  );

  return (
    <div className="page space-y-5 max-w-md">
      {!isOperatorSession && (
        <button
          type="button"
          onClick={() => navigate('/stations')}
          className="inline-flex items-center gap-1 text-sm text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
        >
          <ArrowLeft className="h-4 w-4" /> {t('operator.back')}
        </button>
      )}

      <header>
        <Badge tone="accent">{event?.name ?? t('operator.unknownEvent')}</Badge>
        <h1 className="mt-2 text-3xl font-bold">{station.name}</h1>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {survey?.name
            ? t('operator.surveyActive', { name: survey.name })
            : t('operator.noSurvey')}
        </p>
      </header>

      {/* Big primary actions for the booth operator. */}
      <div className="grid grid-cols-2 gap-3">
        <Button
          variant="primary"
          size="lg"
          // Caso 11 v2.5 — Hand-held operator flow goes straight to the
          // camera scanner; the `?scan=1` query param opens it on mount.
          onClick={() => navigate('/redeem?scan=1')}
          className="flex-col h-24 text-base"
        >
          <Gift className="mb-1 h-6 w-6" />
          {t('operator.cta.redeem')}
        </Button>
        <Button
          variant="secondary"
          size="lg"
          onClick={() => navigate(`/stations/${stationId}/qr`)}
          className="flex-col h-24 text-base"
        >
          <QrCode className="mb-1 h-6 w-6" />
          {t('operator.cta.showQr')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t('operator.stock.title')}</span>
            <span className="text-sm font-normal text-[var(--color-fg-muted)]">
              {t('operator.stock.totalRemaining', { count: totalRemaining })}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {inventory.length === 0 ? (
            <p className="text-sm text-[var(--color-fg-muted)]">
              {t('operator.stock.empty')}
            </p>
          ) : (
            <ul className="divide-y divide-[var(--color-border-subtle)]">
              {inventory.map((row) => (
                <li
                  key={row.id}
                  className="flex items-center justify-between py-2.5"
                >
                  <div className="flex items-center gap-3">
                    {row.stockImage ? (
                      <img
                        src={row.stockImage}
                        alt={row.stockName}
                        className="h-10 w-10 rounded object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded bg-[var(--color-bg-elevated)]" />
                    )}
                    <div>
                      <div className="text-sm font-medium">{row.stockName}</div>
                      {typeof row.rating === 'number' && (
                        <div className="text-xs text-[var(--color-fg-subtle)]">
                          {'★'.repeat(row.rating)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    className={
                      'text-right ' +
                      ((row.available ?? 0) <= 3
                        ? 'text-[var(--color-warn)]'
                        : '')
                    }
                  >
                    <div className="text-lg font-bold tabular-nums">
                      {row.available ?? 0}
                    </div>
                    <div className="text-[10px] uppercase text-[var(--color-fg-muted)]">
                      {t('operator.stock.left')}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {lowStock.length > 0 && (
            <div className="mt-3 rounded-md bg-[var(--color-warn-soft,rgba(251,191,36,0.1))] p-2 text-xs">
              <Sparkles className="mr-1 inline h-3 w-3" />
              {t('operator.stock.lowAlert', { count: lowStock.length })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
